import { useState, useRef, useCallback } from "react";
import { CalendarDays, Send, CheckCircle2, Phone, ImagePlus, X, Camera } from "lucide-react";

interface Props {
  defaultService?: string;
}

const SERVICES = [
  "Möbelmontage & Demontage",
  "Showroom-Einrichtung",
  "Möbeltransport & Lieferung",
  "Zügeln / Umzug",
  "Entsorgung & Rückbau",
  "Operative Logistik / Lager",
  "Elektro & Haushaltsgeräte",
  "Fleckenschutz",
];

const TIME_SLOTS = ["08:00–10:00", "10:00–12:00", "12:00–14:00", "14:00–16:00", "16:00–18:00"];

const MAX_PHOTOS = 6;

export default function BookingForm({ defaultService = "" }: Props) {
  const [sent, setSent] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: defaultService || SERVICES[0],
    date: "",
    time: TIME_SLOTS[0],
    address: "",
    message: "",
  });

  const today = new Date().toISOString().split("T")[0];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const addFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const allowed = Array.from(files).filter((f) => f.type.startsWith("image/"));
    const remaining = MAX_PHOTOS - photos.length;
    const toAdd = allowed.slice(0, remaining);
    if (toAdd.length === 0) return;

    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPreviews((p) => [...p, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
    setPhotos((p) => [...p, ...toAdd]);
  }, [photos.length]);

  const removePhoto = (idx: number) => {
    setPhotos((p) => p.filter((_, i) => i !== idx));
    setPreviews((p) => p.filter((_, i) => i !== idx));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const photoNote = photos.length > 0
      ? `\n📷 Fotos: ${photos.length} Bild(er) werden per WhatsApp nachgesendet (079 888 32 62)\n`
      : "";
    const subject = encodeURIComponent(`Anfrage: ${form.service} — ${form.date}`);
    const body = encodeURIComponent(
      `Guten Tag,\n\nHiermit möchte ich folgende Dienstleistung anfragen:\n\n` +
      `Service: ${form.service}\n` +
      `Datum: ${form.date}\n` +
      `Uhrzeit: ${form.time}\n` +
      `Adresse: ${form.address}\n\n` +
      `Kontakt:\nName: ${form.name}\nTelefon: ${form.phone}\nE-Mail: ${form.email}\n\n` +
      (form.message ? `Bemerkungen:\n${form.message}\n` : "") +
      photoNote +
      `\nFreundliche Grüsse\n${form.name}`
    );
    window.location.href = `mailto:andre.stalder@ds-logistik.ch?subject=${subject}&body=${body}`;
    setSent(true);
  };

  /* ── Ekrani i suksesit ── */
  if (sent) {
    const waText = encodeURIComponent(
      `Hallo DS Logistik,\n\nIch habe soeben eine Anfrage für "${form.service}" gesendet.\nHier schicke ich Ihnen die Fotos dazu.\n\nName: ${form.name}\nTelefon: ${form.phone}`
    );
    return (
      <div className="flex flex-col items-center justify-center gap-5 rounded-3xl border border-green-200 bg-green-50 p-10 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2 className="h-10 w-10 text-green-500" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-foreground">Anfrage gesendet!</h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-sm">
            Vielen Dank, <strong>{form.name}</strong>. Wir melden uns innerhalb von 24 Stunden.
          </p>
        </div>

        {photos.length > 0 && (
          <div className="w-full rounded-2xl border border-green-300 bg-white p-4">
            <p className="text-sm font-semibold text-foreground mb-1">
              📷 Bitte senden Sie Ihre {photos.length} Foto(s) per WhatsApp:
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              So können wir Ihnen eine präzisere Offerte erstellen.
            </p>
            {/* Previews */}
            <div className="flex flex-wrap gap-2 justify-center mb-4">
              {previews.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Foto ${i + 1}`}
                  className="h-16 w-16 rounded-xl object-cover border border-border/40 shadow-sm"
                />
              ))}
            </div>
            <a
              href={`https://wa.me/41798883262?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white shadow-lg hover:scale-105 transition-transform"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.374 0 0 5.373 0 12c0 2.117.549 4.107 1.508 5.84L.057 23.428a.75.75 0 0 0 .915.915l5.589-1.451A11.94 11.94 0 0 0 12 24c6.626 0 12-5.373 12-12S18.626 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.027-1.384l-.36-.214-3.733.968.99-3.617-.234-.373A9.798 9.798 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/>
              </svg>
              Fotos jetzt per WhatsApp senden
            </a>
          </div>
        )}

        <button
          onClick={() => { setSent(false); setPhotos([]); setPreviews([]); }}
          className="rounded-full border border-border/60 px-5 py-2 text-sm font-semibold hover:bg-muted transition-colors"
        >
          Neue Anfrage
        </button>
      </div>
    );
  }

  /* ── Forma kryesore ── */
  return (
    <div
      id="termin-anfragen"
      className="rounded-3xl border border-border/60 bg-card p-6 sm:p-8"
      style={{ boxShadow: "0 16px 48px -24px rgba(37,10,8,0.18)" }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="rounded-2xl bg-primary/10 p-2.5 text-primary">
          <CalendarDays className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-foreground">Termin & Anfrage</h3>
          <p className="text-xs text-muted-foreground">Wählen Sie Service, Datum und Uhrzeit</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Service */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-foreground/70 uppercase tracking-wide">
            Dienstleistung
          </label>
          <select
            name="service"
            value={form.service}
            onChange={handleChange}
            className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            required
          >
            {SERVICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Datum + Zeit */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground/70 uppercase tracking-wide">
              Datum
            </label>
            <input
              type="date"
              name="date"
              value={form.date}
              min={today}
              onChange={handleChange}
              className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground/70 uppercase tracking-wide">
              Uhrzeit
            </label>
            <select
              name="time"
              value={form.time}
              onChange={handleChange}
              className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Name + Telefon */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground/70 uppercase tracking-wide">
              Name
            </label>
            <input
              id="booking-name"
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ihr Name"
              className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground/70 uppercase tracking-wide">
              Telefon
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+41 ..."
              className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-foreground/70 uppercase tracking-wide">
            E-Mail
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="ihre@email.ch"
            className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            required
          />
        </div>

        {/* Adresse */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-foreground/70 uppercase tracking-wide">
            Adresse / Ort
          </label>
          <input
            type="text"
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="Strasse, PLZ, Ort"
            className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Bemerkungen */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-foreground/70 uppercase tracking-wide">
            Bemerkungen (optional)
          </label>
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
            rows={3}
            placeholder="Weitere Details zu Ihrem Auftrag..."
            className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          />
        </div>

        {/* ── Foto Upload ── */}
        <div>
          <label className="mb-2 flex items-center gap-2 text-xs font-semibold text-foreground/70 uppercase tracking-wide">
            <Camera className="h-3.5 w-3.5 text-primary" />
            Fotos der Möbel (optional · max. {MAX_PHOTOS} Bilder)
          </label>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => photos.length < MAX_PHOTOS && fileInputRef.current?.click()}
            className={`relative flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-5 text-center transition-all cursor-pointer
              ${dragging
                ? "border-primary bg-primary/5 scale-[1.01]"
                : photos.length >= MAX_PHOTOS
                  ? "border-border/30 bg-muted/30 cursor-not-allowed opacity-60"
                  : "border-border/50 bg-muted/20 hover:border-primary/60 hover:bg-primary/5"
              }`}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ImagePlus className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {photos.length >= MAX_PHOTOS
                  ? "Maximale Anzahl erreicht"
                  : dragging
                    ? "Hier ablegen..."
                    : "Fotos hinzufügen"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {photos.length >= MAX_PHOTOS
                  ? `${MAX_PHOTOS} von ${MAX_PHOTOS} Fotos ausgewählt`
                  : "Klicken oder per Drag & Drop — JPG, PNG, HEIC"}
              </p>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />

          {/* Previews */}
          {previews.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {previews.map((src, i) => (
                <div key={i} className="group relative aspect-square overflow-hidden rounded-xl border border-border/40 shadow-sm">
                  <img
                    src={src}
                    alt={`Foto ${i + 1}`}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    title="Foto entfernen"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-1 py-0.5 text-center text-[9px] text-white/90">
                    {i + 1}/{MAX_PHOTOS}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info banner */}
          {photos.length > 0 && (
            <div className="mt-2 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
              <span className="text-amber-500 text-sm mt-0.5">ℹ️</span>
              <p className="text-xs text-amber-700 leading-relaxed">
                <strong>{photos.length} Foto(s) ausgewählt.</strong> Nach dem Absenden werden Sie per WhatsApp aufgefordert, die Fotos direkt zu senden — so erhalten Sie eine präzisere Offerte.
              </p>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="submit"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white shadow-[0_8px_24px_-10px_rgba(200,25,11,0.6)] hover:-translate-y-px transition-all"
          >
            <Send className="h-4 w-4" />
            {photos.length > 0 ? `Anfrage + ${photos.length} Foto(s) senden` : "Anfrage absenden"}
          </button>
          <a
            href="tel:+41326712010"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-border/60 px-6 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
          >
            <Phone className="h-4 w-4 text-primary" /> 032 671 20 10
          </a>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Kostenlose Offerte — wir melden uns innerhalb von 24 Stunden.
        </p>
      </form>
    </div>
  );
}
